// @flow
import moment from 'moment';
import { isEmpty, isNil, pathOr } from 'ramda';
import _ from 'lodash';
import { sortByDateField } from './date-helpers';
import HUD_SUBSIDY_CODES from '../constants/hudSubsidyCodes';
import { MOVE_IN_CERT_TYPE } from '../constants/affordableCertificationTypes';

export type HouseholdTransferLeaseValidations = {
  hasActiveTransferLease: boolean,
  isValid: boolean,
};

export const COMPLIANCE_APPROVAL_STATUSES = {
  APPROVED: 'Approved',
  APPROVED_PENDING_SIGN: 'Approved Pending Signatures',
  CERTIFICATION_REOPENED: 'Certification Reopened',
  CORRECTION_NEEDED: 'Correction Needed',
  CORRECTION_NEEDED_FINAL: 'Correction Needed - Final Packet',
  DENIED: 'Denied',
  PENDING: 'Pending',
  ON_NOTICE_HOLD: 'Household on Notice / Hold',
  NONE: 'None', // not defined in the DB, but used in our code
  NOT_STARTED: 'Not Started', // not defined in the DB, but used in our code
  STARTED: 'Started', // not defined in the DB, but used in our code
  COMPLETE: 'Complete', // not defined in the DB, but used in our code
  PENDING_FINAL_APPROVAL: 'Pending Final Approval',
};

export const NOT_STARTED_COMPLIANCE_APPROVAL = {
  id: 'NotStarted',
  name: 'Not Started',
};

export const AFFORDABLE_QUALIFICATION_STATUSES = {
  ACTIVE: 'ACTIVE',
  REOPEN: 'REOPEN',
  COMPLETE: 'COMPLETE',
};

export const AFFORDABLE_PROGRAMS = {
  BOND: 'BOND',
  CDGB: 'CDGB',
  CITC: 'CITC',
  HOME: 'HOME',
  HUD: 'HUD',
  LIHTC: 'LIHTC',
  LIHTC_HOME: 'LIHTC/HOME',
  'LIHTC/HOME': 'LIHTC/HOME',
  RD: 'RD',
  NOAH: 'NOAH',
  DeferredLoan: 'Deferred Loan',
  VHCB: 'VHCB',
  NSP: 'NSP',
  AHP: 'AHP',
  KCMO: 'KCMO',
  StLouisFunds: 'St. Louis Funds',
  HTF: 'HTF',
  TCAP: 'TCAP',
  PH: 'PH',
  PBV: 'PBV',
};

export const MANAGEMENT_FEE_LABELS = {
  RDNonContinuousParcels: 'Non-Contiguous Parcels of Land',
  RDRemoteLocation: 'Remote Location',
  RDTroubledOrCritical: 'Troubled or Critical Property',
};

export const AFFORDABLE_RELATIONSHIPS = {
  ADULT_CO_TENANT: 'Adult co-tenant',
  CHILD: 'Child',
  FOSTER_CHILD_ADULT: 'Foster child/adult',
  GUARANTOR: 'Guarantor',
  HEAD_OF_HOUSEHOLD: 'Head of Household',
  LIVE_IN_CARETAKER: 'Live-in Caretaker',
  OTHER_FAMILY_MEMBER: 'Other Family Member',
  SPOUSE: 'Spouse',
  UNBORN_CHILD: 'Unborn Child',
};

/**
 * Checks if a household member/applicant is an unborn child.
 * Handles both nested (affordableRelationship.name) and flat (affordableRelationship) shapes.
 */
export const isUnbornChild = (entity) =>
  entity?.affordableRelationship?.name ===
    AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD ||
  entity?.affordableRelationship === AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD;

export const CUSTOMER_STATUSES = {
  APPLICANT: 'Applicant',
  RESIDENT: 'Current Resident',
  PRIOR_RESIDENT: 'Prior Resident',
};

// Mapping PH/PBV certification types to our internal affordable qualification types
export const PH_PBV_CERTIFICATION_TYPES_MAPPING = {
  MOVE_IN: 'NEW_ADMISSION',
  RECERT: 'ANNUAL_REEXAMINATION',
  INTERIM: 'INTERIM_REEXAMINATION',
  MOVE_OUT: 'END_OF_PARTICIPATION',
  UNIT_TRANSFER: 'OTHER_CHANGE_OF_UNIT',
};

export const getIsReceivingAssistance = (programs: Array<Object>): boolean => {
  return (programs || []).reduce((acc, program) => {
    if (acc === true) return acc;
    return pathOr(
      false,
      ['affordableQualification', 'receivingAssistance'],
      program,
    );
  }, false);
};

export const getAffordableFloorPlanProgramTypes = (floorPlan: Object) => {
  return pathOr([], ['floorPlanAffordablePrograms'], floorPlan).reduce(
    (acc, program) => {
      const affordableProgram = pathOr(
        '',
        ['propertyAffordableProgram', 'masterAffordableProgram', 'name'],
        program,
      );
      acc[`is${affordableProgram}FloorPlan`] = true;
      return acc;
    },
    {
      isHUDFloorPlan: false,
      isLIHTCFloorPlan: false,
      isRDFloorPlan: false,
      isPHFloorPlan: false,
    },
  );
};

export function getAggregatedProgramTypes(floorPlans = []) {
  return floorPlans.reduce(
    (acc, floorPlan) => {
      const result = getAffordableFloorPlanProgramTypes(floorPlan);
      acc.isHUDFloorPlan = acc.isHUDFloorPlan || result.isHUDFloorPlan;
      acc.isLIHTCFloorPlan = acc.isLIHTCFloorPlan || result.isLIHTCFloorPlan;
      acc.isRDFloorPlan = acc.isRDFloorPlan || result.isRDFloorPlan;
      return acc;
    },
    {
      isHUDFloorPlan: false,
      isLIHTCFloorPlan: false,
      isRDFloorPlan: false,
    },
  );
}

const findMatchingFPUtilityAllowanceByDate = (
  fpUtilityAllowances: Array<Object>,
  voucherEffectiveDate: Object,
) => {
  return fpUtilityAllowances.reduce((current, fpAllowance) => {
    const endDate = pathOr(null, ['endDate'], fpAllowance);
    const startDate = pathOr(null, ['startDate'], fpAllowance);

    const mEnDate = moment(endDate);
    const mStartDate = moment(startDate);
    const mEffectiveDate = moment(voucherEffectiveDate);

    if (endDate === null && mEffectiveDate.isSameOrAfter(mStartDate)) {
      return fpAllowance;
    }

    if (mEffectiveDate.isAfter(mEnDate)) {
      return current;
    }

    if (mEffectiveDate.isBetween(mStartDate, mEnDate)) {
      return fpAllowance;
    }

    if (mEffectiveDate.isBefore(mStartDate)) {
      return current;
    }

    return current;
  }, null);
};

export const determineAffordableUtilityAllowances = (
  floorPlan: Object,
  voucherEffectiveDate: Object,
) => {
  const allowances = pathOr(null, ['allowances'], floorPlan);
  const allowancesHUD =
    allowances &&
    allowances.filter((allowance) =>
      pathOr(false, ['pha', 'isHUD'], allowance),
    );
  const allowance =
    allowancesHUD && allowancesHUD.length > 0 && allowancesHUD[0];

  /* If there is no Allowance, then there won't be a fpua either */
  if (!allowance) {
    return null;
  }

  const fpUtilityAllowances = pathOr(null, ['fpua'], floorPlan);
  const fpUtilityAllowancesHUD =
    fpUtilityAllowances &&
    // data coming from floorplan results
    fpUtilityAllowances.filter((ua) => {
      return pathOr(
        pathOr(false, ['allowances', 'pha', 'isHUD'], ua),
        ['ua', 'pha', 'isHUD'],
        ua,
      );
    });

  const matchingFPAllowance =
    fpUtilityAllowancesHUD &&
    fpUtilityAllowancesHUD.length > 0 &&
    findMatchingFPUtilityAllowanceByDate(
      fpUtilityAllowancesHUD,
      voucherEffectiveDate,
    );

  return matchingFPAllowance
    ? pathOr(null, ['feeAmount'], matchingFPAllowance)
    : pathOr(null, ['utilityAllowanceAmount'], allowance);
};

export const getHUDContractRent = (
  floorPlan: Object,
  currentLeaseStartDate: Object,
) => {
  const HUDUtilityAllowance = currentLeaseStartDate
    ? +determineAffordableUtilityAllowances(
        floorPlan,
        moment(currentLeaseStartDate),
      )
    : 0;
  const HUDGrossRent = pathOr(0, ['HUDGrossRent'], floorPlan);
  return +HUDGrossRent - +HUDUtilityAllowance;
};

export const getNonOptionalChargeFromQualifications = (
  affordableQualifications: Array<Object>,
): number | null => {
  const latestActiveLIHTCCert = (affordableQualifications ?? []).find(
    (aq) =>
      aq?.propertyAffordableProgram?.masterAffordableProgram?.name === 'LIHTC',
  );

  if (isEmpty(latestActiveLIHTCCert ?? {})) {
    const completedCerts =
      affordableQualifications?.filter((aq) => aq.isActive) ?? [];
    const lastCompletedLIHTCCert = sortByDateField(
      completedCerts ?? [],
      'voucherEffectiveDate',
      true,
    ).filter(
      (aq) =>
        aq?.propertyAffordableProgram?.masterAffordableProgram?.name ===
        'LIHTC',
    )?.[0];

    return lastCompletedLIHTCCert?.nonOptionalCharge;
  }

  return latestActiveLIHTCCert?.nonOptionalCharge;
};

export const getMostRecentActiveAffordableQualification = (
  affordableQualifications: Array<Object>,
) => {
  const filterOutCompletedCerts =
    affordableQualifications?.filter((aq) => aq.isActive) ?? [];
  const latestActiveCert =
    sortByDateField(
      filterOutCompletedCerts ?? [],
      'voucherEffectiveDate',
      true,
    )?.[0] ?? {};
  return latestActiveCert;
};

export const getCurrentNonOptionalCharge = (
  fpNonOptionalCharges: Array<Object>,
): number =>
  fpNonOptionalCharges.filter(
    (nonOptionalCharge) =>
      isNil(nonOptionalCharge.deletedAt) &&
      moment.utc(nonOptionalCharge.startDate).isBefore(moment.utc()) &&
      moment
        .utc(nonOptionalCharge.endDate ?? undefined)
        .isSameOrAfter(moment.utc()),
  )?.[0]?.feeAmount ?? 0;

export const chkAllTabsApproved = (
  allAffordableTabs: Array<Object>,
  affordableQualifications: Array<Object>,
): boolean => {
  if (affordableQualifications === undefined) {
    return true;
  }
  const allPass = allAffordableTabs.every((tab) => {
    if (!tab) {
      return true;
    }
    const approvalStatus =
      tab?.affordableQualification?.complianceApproval?.name ?? '';
    return approvalStatus.includes('Approved');
  });
  return allPass;
};

/**
 * Renders affordable program names as a human-readable list: "HUD", "HUD and RD",
 * "HUD, LIHTC and RD", "HUD, LIHTC, HOME and RD".
 *
 * Product picked this style over `Intl.ListFormat`, which for en-US inserts an Oxford comma
 * ("HUD, LIHTC, and RD"). The backend has a matching helper
 * (fortress-backend src/utils/formatProgramList.ts) because the transfer gate builds its copy on
 * the server for the 400 rejection body while the tooltip is built here. Change both together.
 */
export const formatProgramList = (names: Array<string>): string => {
  const list = (Array.isArray(names) ? names : []).filter((name) =>
    Boolean(name && name.trim()),
  );
  if (!list.length) {
    return '';
  }
  if (list.length === 1) {
    return list[0];
  }
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

/**
 * The master affordable program names behind chkAllTabsApproved's `false`, so the gate tooltip can
 * name what is actually blocking instead of saying "all applicable certifications".
 *
 * Deliberately mirrors chkAllTabsApproved's predicate rather than sharing it: the two must agree
 * on what counts as approved (`name` containing 'Approved'), on skipping falsy tabs, and on
 * treating an unloaded qualification list as not-blocking so the buttons are not disabled mid-fetch.
 * Kept in tab order, which is the order the user sees the tabs in.
 *
 * A tab whose `programName` is missing is omitted — there is nothing to render for it, and
 * chkAllTabsApproved still returns false, so the buttons stay disabled either way.
 */
export const getUnapprovedProgramNames = (
  allAffordableTabs: Array<Object>,
  affordableQualifications: Array<Object>,
): Array<string> => {
  if (affordableQualifications === undefined) {
    return [];
  }
  const unapproved = (allAffordableTabs ?? []).reduce((acc, tab) => {
    if (!tab) {
      return acc;
    }
    const approvalStatus =
      tab?.affordableQualification?.complianceApproval?.name ?? '';
    if (approvalStatus.includes('Approved')) {
      return acc;
    }
    const programName = tab?.programName ?? '';
    if (!programName || acc.includes(programName)) {
      return acc;
    }
    return [...acc, programName];
  }, []);
  return unapproved;
};

/**
 * ENG-32187 / ENG-32886 — the Move-In gate on the generate/execute lease buttons (never Save),
 * evaluating each program's ACTIVE MOVE_IN
 * cert (getUnapprovedProgramNames/chkAllTabsApproved instead judge whichever cert the tab-display
 * logic surfaced, and for a multi-cert HUD tab that is deliberately the MOVE_OUT/TERMINATION/
 * INTERIM/correction cert — not the move-in one), but scopes to the qualification tabs the user
 * actually sees.
 *
 * ENG-32886: a layered/mixed property carries one raw MOVE_IN qualification per master program —
 * the individual programs (LIHTC, HOME, Deferred Loan), every composite (LIHTC/Deferred Loan) and
 * the layered one (LIHTC/HOME/Deferred Loan) — but `configureResidentApplicantAffordableTabsDisplay`
 * consolidates those into the few tabs bound to the floor plan. Reading every raw record listed
 * phantom program names in the tooltip and, worse, demanded approval on certs that map to no
 * approvable tab, so the gated buttons never re-enabled. We therefore join move-in certs to the visible tabs by
 * `propertyAffordableProgramId`: a tab and its surfaced qualification share that id (per-papId the
 * master program name is constant, so the HUD gap still evaluates the move-in cert, not the tab's
 * surfaced one), and phantom certs whose papId maps to no visible tab are ignored.
 *
 * `MOVE_IN_CERT_TYPE` is the stored certificationType of the first/move-in cert across HUD, LIHTC,
 * RD and PH/PBV (RD and PH label it "Initial"/"New Admission" but persist 'MOVE_IN'); the INITIAL
 * and *_UT transfer types are intentionally excluded.
 *
 * Approval mirrors chkAllTabsApproved: a compliance-approval name containing 'Approved' (i.e.
 * Approved or Approved Pending Signatures). An undefined qualification list means the profile is
 * still fetching — treated as approved so the gated buttons are not disabled mid-load.
 */
export const chkAllMoveInCertsApproved = (
  allAffordableTabs: Array<Object>,
  affordableQualifications: Array<Object>,
): boolean => {
  if (affordableQualifications === undefined) {
    return true;
  }
  const tabPapIds = new Set(
    (allAffordableTabs ?? [])
      .map((tab) => tab?.propertyAffordableProgramId)
      .filter(Boolean),
  );
  return (affordableQualifications ?? [])
    .filter(
      (aq) =>
        aq?.isActive &&
        aq?.certificationType === MOVE_IN_CERT_TYPE &&
        tabPapIds.has(aq?.propertyAffordableProgramId),
    )
    .every((aq) => (aq?.complianceApproval?.name ?? '').includes('Approved'));
};

/**
 * The qualification-tab names behind chkAllMoveInCertsApproved's `false`, so the lease-action button tooltip
 * can name each visible tab whose active Move-In cert is unapproved. Iterates the visible tabs (so
 * the copy uses the consolidated tab name the user sees and stays in tab order), joining move-in
 * certs by `propertyAffordableProgramId`. A tab with no active move-in cert does not block; a tab
 * whose `programName` is missing is omitted, but chkAllMoveInCertsApproved still returns false, so
 * the buttons stay disabled either way.
 */
export const getUnapprovedMoveInProgramNames = (
  allAffordableTabs: Array<Object>,
  affordableQualifications: Array<Object>,
): Array<string> => {
  if (affordableQualifications === undefined) {
    return [];
  }
  const moveInCerts = (affordableQualifications ?? []).filter(
    (aq) => aq?.isActive && aq?.certificationType === MOVE_IN_CERT_TYPE,
  );
  return (allAffordableTabs ?? []).reduce((acc, tab) => {
    const papId = tab?.propertyAffordableProgramId;
    const programName = tab?.programName ?? '';
    if (!papId || !programName || acc.includes(programName)) {
      return acc;
    }
    const hasUnapprovedMoveInCert = moveInCerts.some(
      (aq) =>
        aq?.propertyAffordableProgramId === papId &&
        !(aq?.complianceApproval?.name ?? '').includes('Approved'),
    );
    if (!hasUnapprovedMoveInCert) {
      return acc;
    }
    return [...acc, programName];
  }, []);
};

// Returns true if one tab has status of list passed
export const chkAllTabsForComplianceApprovalStatuses = (
  allAffordableTabs: Array<Object>,
  affordableQualifications: Array<Object>,
  approvalStatusesToMatch: Array<string>,
): boolean => {
  if (affordableQualifications === undefined) {
    return false;
  }
  const anyMatches = allAffordableTabs.some((tab) => {
    if (!tab) {
      return false;
    }
    const approvalStatus =
      tab?.affordableQualification?.complianceApproval?.name ?? '';
    return approvalStatusesToMatch.includes(approvalStatus);
  });
  return anyMatches;
};

export const getAffordableAndNonAffordableMembers = (
  householdMembers: Array<Object>,
  excludeFosterOccupants: boolean,
) => {
  const fExcludeFosterOccupants = (applicant) => {
    if (!excludeFosterOccupants) {
      return true;
    }
    return applicant?.ar?.name !== AFFORDABLE_RELATIONSHIPS.FOSTER_CHILD_ADULT;
  };

  const [affordableMembers, nonAffordableMembers] = _.partition(
    householdMembers,
    (applicant) =>
      applicant?.applicantType?.name !== 'Pet' &&
      applicant?.applicantType?.name !== 'Guarantor Lease Signer' &&
      applicant?.ar?.name !== AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER &&
      applicant?.ar?.name !== AFFORDABLE_RELATIONSHIPS.GUARANTOR &&
      applicant?.applicantMinor?.jointCustody !== 'yesLessThan50' &&
      fExcludeFosterOccupants(applicant),
  );

  return { affordableMembers, nonAffordableMembers };
};

export const formatAffordableMembersForCertModal = (members: Array<Object>) => {
  return members.map((member) => ({
    id: member.id,
    name: member?.name ?? '',
    relationship: member?.ar?.name ?? '',
  }));
};

export const formatNonAffordableMembersForCertModal = (
  members: Array<Object>,
  applyHotmaRules = false,
) => {
  return members.map((member) => {
    // TODO: add translations for tooltips
    let tooltip = '';
    if (member?.applicantType.name === 'Guarantor Lease Signer') {
      tooltip = 'Guarantor Lease Signer';
    } else if (
      member?.ar?.name === AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER
    ) {
      tooltip = AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER;
    } else if (member?.ar?.name === AFFORDABLE_RELATIONSHIPS.GUARANTOR) {
      tooltip = AFFORDABLE_RELATIONSHIPS.GUARANTOR;
    } else if (member?.applicantMinor?.jointCustody === 'yesLessThan50') {
      tooltip = 'Child with Joint Custody under 50%';
    } else if (applyHotmaRules && member?.ar?.name === 'Foster child/adult') {
      tooltip = 'Foster Child/Adult';
    }
    return {
      id: member.id,
      name: member?.name ?? '',
      relationship: member?.ar?.name ?? '',
      tooltip,
    };
  });
};

export const getIsSection236 = (
  property: Object,
  floorPlan: Object,
): boolean => {
  const hasHUD = (property?.pap ?? [])
    .map((pap) => pap?.masterAffordableProgram?.name)
    .includes(AFFORDABLE_PROGRAMS.HUD);
  if (hasHUD) {
    const fpHudCode = (floorPlan?.floorPlanAffordablePrograms ?? []).find(
      (fp) =>
        fp?.propertyAffordableProgram?.masterAffordableProgram?.name ===
        AFFORDABLE_PROGRAMS.HUD,
    )?.hudCode;
    const propertyHUDSubsidy = property?.setup?.hudPrimarySubsidyType?.code;

    return (
      propertyHUDSubsidy === HUD_SUBSIDY_CODES.SECTION_236 ||
      fpHudCode === HUD_SUBSIDY_CODES.SECTION_236
    );
  }

  return false;
};

const ASSET_THRESHOLD_DEFAULT = 50000;
export const getAssetThreshold = (
  affordableConfigs: Object,
  programName: string,
  isHotmaActive: boolean,
) => {
  if (isHotmaActive) {
    return affordableConfigs?.ASSET_THRESHOLD_HOTMA ?? ASSET_THRESHOLD_DEFAULT;
  }

  return (
    affordableConfigs?.[`ASSET_THRESHOLD_${programName}`] ??
    affordableConfigs?.ASSET_THRESHOLD ??
    ASSET_THRESHOLD_DEFAULT
  );
};

export const getURLProgramName = (programName) =>
  (programName ?? '').replace(/\//g, '_');

// Sits alongside the affordable program names in the Selected Program(s) table
// and in the Waitlist Summary card's Income Levels list.
export const CONVENTIONAL_PROGRAM_NAME = 'Conventional';

// A layered property's funding is stored as a single MasterAffordableProgram
// whose name carries the layering — `LIHTC/HOME` — with no components table and
// no isLayered flag to read instead. Splitting the name is the only way to the
// individual programs, and the waitlist configuration only ever holds those
// (ViewProperty/AffordableSetup offers atomic names alone).
//
// `/` only: `LIHTC & HUD` exists as a master record but no property carries an
// `&` name, and both the waitlist-config picker and the backend's component map
// split on `/` alone.
export const toAtomicProgramNames = (
  programNames: ?$ReadOnlyArray<?string>,
): Array<string> =>
  Array.from(
    new Set(
      (programNames ?? [])
        .filter(Boolean)
        .flatMap((name) => name.split('/'))
        .map((name) => name.trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

export const filterInvalidApplicants = (applicants, excludeFoster) => {
  return applicants.filter((applicant) => {
    const isNotFoster = excludeFoster
      ? applicant?.affordableRelationship?.name !==
        AFFORDABLE_RELATIONSHIPS.FOSTER_CHILD_ADULT
      : true;
    const isNotGuarantor =
      applicant?.affordableRelationship?.name !==
      AFFORDABLE_RELATIONSHIPS.GUARANTOR;
    const isNotLiveInCaretaker =
      applicant?.affordableRelationship?.name !==
      AFFORDABLE_RELATIONSHIPS.LIVE_IN_CARETAKER;
    // ENG-22181: Exclude unborn children from Citizenship modal
    const isNotUnbornChild =
      applicant?.affordableRelationship?.name !==
      AFFORDABLE_RELATIONSHIPS.UNBORN_CHILD;
    return (
      isNotFoster && isNotGuarantor && isNotLiveInCaretaker && isNotUnbornChild
    );
  });
};

export type ApplicationDateRange = { start: ?string, end: string };

export type ApplicationWindow = {
  hasPolicy: boolean,
  state: ?string,
  range: ?ApplicationDateRange,
  isClosed: boolean,
  closeDate: ?string,
  acceptsAnyDate: boolean,
};

const DAY_MS = 24 * 60 * 60 * 1000;

// An open date after the end: the window has not opened, so no date falls inside
// it. Mirrors the backend's getApplicationDateRefusal.
export const hasApplicationDateRangeNotOpened = ({
  start,
  end,
}: ApplicationDateRange): boolean =>
  start ? new Date(start).getTime() > new Date(end).getTime() : false;

// Mirrors isWithinApplicationDateRange in the backend's waitlistPolicy. `end` is the start of its
// local day, so the whole end day is inclusive. On an open waitlist `end` is the time the
// configuration was fetched, and the same day of slack keeps a date seeded after that fetch inside.
// The open date bounds only a household's first entry; one already on the waitlist keeps its
// original date, which may predate it, so `enforceStart: false` drops that bound.
export const isWithinApplicationDateRange = (
  range: ?ApplicationDateRange,
  date: ?(Date | string),
  { enforceStart = true }: { enforceStart?: boolean } = {},
): boolean => {
  if (!range || date == null || hasApplicationDateRangeNotOpened(range)) {
    return false;
  }

  const time = new Date(date).getTime();
  if (Number.isNaN(time)) return false;
  if (enforceStart && range.start && time < new Date(range.start).getTime()) {
    return false;
  }

  return time < new Date(range.end).getTime() + DAY_MS;
};

export const getApplicationWindow = (
  configuration: ?Object,
): ApplicationWindow => {
  const policy = configuration?.policy;
  const range = policy?.applicationDateRange ?? null;

  return {
    hasPolicy: Boolean(policy),
    state: policy?.state ?? null,
    range,
    isClosed: policy?.effectiveStatus === 'CLOSED',
    closeDate: configuration?.scheduledCloseDate ?? null,
    acceptsAnyDate: range != null && !hasApplicationDateRangeNotOpened(range),
  };
};
